import { all, fork, put, takeLatest, takeLeading } from '@redux-saga/core/effects';
import { call, cancelled, delay } from 'redux-saga/effects';

import { apiServiceUrl } from '../../constants';
import {
  fetchJSON,
  fetchGetMethod,
  fetchPostMethod,
  fetchGetMethodAsync,
  fetchPostMethodWithFormData,
  fetchBasicPostMethodWithToken,
  GetErrorMessageFromStatus,
} from '../../helpers/api';
import { getApiErrorMessage } from '../../helpers/apiErrorMessage';
import { addNotification } from '../../helpers/notification';
import { getLoggedInUser } from '../../helpers/authUtils';
import * as Actions from './actions';
import * as ActionsType from './constants';

const organizationApiServiceUrl = `${apiServiceUrl}/organization`;
const dashboardApiServiceUrl = `${apiServiceUrl}/dashboard`;

// Organization Details.
function* loadOrganizationDetails() {
  try {
    const user = getLoggedInUser();
    
    if (!user) {
      yield put(Actions.loadOrganizationDetailsFailed('User not authenticated'));
      return;
    }
    
    // Skip API call if user is on free plan (0 = New, 1 = Free)
    // Organization features are only available for Premium (2) and Executive (3) plans
    const currentPlan = user.currentPlan;
    if (currentPlan === 0 || currentPlan === 1 || !currentPlan) {
      // Don't make the API call for free plan users
      // Dispatch success with null to clear loading state without showing errors
      yield put(Actions.loadOrganizationDetailsSuccess(null));
      return;
    }
    
    const response = yield call(
      fetchGetMethod,
      `${organizationApiServiceUrl}/GetOrganizationDetails`,
      user.token
    );
    if (response.status === 200) {
      const { organization } = yield response.data
        .then((data) => ({ organization: data?.organization }))
        .catch(() => ({ organization: null }));
      // 200 with null = no org or no access (backend returns this instead of 400)
      yield put(Actions.loadOrganizationDetailsSuccess(organization ?? null));
    } else {
      // 400 e.g. deactivated org - show message; or treat as no org to avoid stuck UI
      const msg = GetErrorMessageFromStatus(response);
      if (msg && msg.includes("don't have an organization")) {
        yield put(Actions.loadOrganizationDetailsSuccess(null));
      } else {
        yield put(Actions.loadOrganizationDetailsFailed(msg));
      }
    }
  } catch (error) {
    yield put(Actions.loadOrganizationDetailsFailed(GetErrorMessageFromStatus(error)));
  }
}

function* createOrganization({ payload: { organizationDetails } }) {
  const user = getLoggedInUser();
  const data = new FormData();
  data.append('AddressLine1', organizationDetails.AddressLine1);
  data.append('AddressLine2', organizationDetails.AddressLine2);
  data.append('Country', organizationDetails.Country);
  data.append('Email', organizationDetails.Email);
  data.append('Name', organizationDetails.Name);
  data.append('File', organizationDetails.File);
  try {
    const response = yield call(
      fetchPostMethodWithFormData,
      `${organizationApiServiceUrl}/CreateOrganization`,
      user.token,
      data
    );

    if (response.status === 200) {
      const responseData = yield response.data;
      console.log('Create Organization API Response (raw):', responseData);
      
      const successMessage = responseData?.message || responseData?.successMessage;
      const newToken = responseData?.token;
      const organizationId = responseData?.organizationId || responseData?.organization?.id;
      const showConfirmation = responseData?.showConfirmation === true || responseData?.showConfirmation === 'true';
      const transferredVPsCount = responseData?.transferredVPsCount || 0;
      const transferredClientsCount = responseData?.transferredClientsCount || 0;

      console.log('Extracted values:', { 
        successMessage, 
        newToken, 
        organizationId, 
        showConfirmation, 
        transferredVPsCount, 
        transferredClientsCount 
      });

      if (successMessage && newToken) {
        // If organizationId is not in the response, try to get it from organization details
        if (!organizationId) {
          try {
            console.log(
              'OrganizationId not found in response, trying to get it from organization details...'
            );
            const orgDetailsResponse = yield call(
              fetchGetMethod,
              `${organizationApiServiceUrl}/GetOrganizationDetails`,
              newToken
            );
            if (orgDetailsResponse.status === 200) {
              const { organization } = yield orgDetailsResponse.data
                .then((response) => ({ organization: response.organization }))
                .catch(() => ({ organization: null }));
              if (organization?.id) {
                console.log('Found organizationId from details:', organization.id);
                yield put(
                  Actions.createOrganizationSuccess(
                    newToken, 
                    successMessage, 
                    organization.id,
                    showConfirmation,
                    transferredVPsCount,
                    transferredClientsCount
                  )
                );
              } else {
                console.log('Organization details not found, proceeding without organizationId');
                yield put(Actions.createOrganizationSuccess(
                  newToken, 
                  successMessage, 
                  null,
                  showConfirmation,
                  transferredVPsCount,
                  transferredClientsCount
                ));
              }
            } else {
              console.log('Failed to get organization details, proceeding without organizationId');
              yield put(Actions.createOrganizationSuccess(
                newToken, 
                successMessage, 
                null,
                showConfirmation,
                transferredVPsCount,
                transferredClientsCount
              ));
            }
          } catch (error) {
            console.log('Error getting organization details:', error);
            yield put(Actions.createOrganizationSuccess(
              newToken, 
              successMessage, 
              null,
              showConfirmation,
              transferredVPsCount,
              transferredClientsCount
            ));
          }
        } else {
          yield put(Actions.createOrganizationSuccess(
            newToken, 
            successMessage, 
            organizationId,
            showConfirmation,
            transferredVPsCount,
            transferredClientsCount
          ));
        }
      } else {
        yield put(Actions.createOrganizationFailed(GetErrorMessageFromStatus(response)));
      }
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response.message }));
      yield put(Actions.createOrganizationFailed(message ?? GetErrorMessageFromStatus(response)));
    } else {
      yield put(Actions.createOrganizationFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.createOrganizationFailed(GetErrorMessageFromStatus(error)));
  }
}

function* editOrganizationDetails({ payload: { organizationDetails } }) {
  const user = getLoggedInUser();
  const data = new FormData();
  data.append('Id', organizationDetails.id);
  data.append('AddressLine1', organizationDetails.AddressLine1);
  data.append('AddressLine2', organizationDetails.AddressLine2);
  data.append('Country', organizationDetails.Country);
  data.append('Email', organizationDetails.Email);
  data.append('Name', organizationDetails.Name);
  data.append('File', organizationDetails.File);

  try {
    const response = yield call(
      fetchPostMethodWithFormData,
      `${organizationApiServiceUrl}/CreateOrganization`,
      user.token,
      data
    );
    if (response.status === 200) {
      const { successMessage, status } = yield response.data
        .then((response) => ({ successMessage: response }))
        .catch(() => ({ status: 500 }));
      if (successMessage) {
        yield put(Actions.editOrganizationDetailsSuccess(organizationDetails, successMessage));
      } else {
        yield put(
          Actions.editOrganizationDetailsFailed(GetErrorMessageFromStatus({ status: status }))
        );
      }
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response.message }));
      yield put(
        Actions.editOrganizationDetailsFailed(message ?? GetErrorMessageFromStatus(response))
      );
    } else {
      yield put(Actions.editOrganizationDetailsFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.editOrganizationDetailsFailed(GetErrorMessageFromStatus(error)));
  }
}

// Organization clients.
function* loadOrganizationClients() {
  try {
    const user = getLoggedInUser();
    const response = yield call(
      fetchGetMethod,
      `${organizationApiServiceUrl}/GetClientsDetails`,
      user.token
    );
    if (response.status === 200) {
      const { organizationClients, status } = yield response.data
        .then((response) => ({ organizationClients: response }))
        .catch(() => ({ status: 500 }));
      if (organizationClients) {
        yield put(Actions.loadOrganizationClientsSuccess(organizationClients));
      } else {
        yield put(
          Actions.loadOrganizationClientsFailed(GetErrorMessageFromStatus({ status: status }))
        );
      }
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response.message }));
      yield put(
        Actions.loadOrganizationClientsFailed(message ?? GetErrorMessageFromStatus(response))
      );
    } else {
      yield put(Actions.loadOrganizationClientsFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.loadOrganizationClientsFailed(GetErrorMessageFromStatus(error)));
  }
}

function* loadOrganizationMainClient({ payload: { mainClientId } }) {
  try {
    const user = getLoggedInUser();
    const response = yield call(
      fetchGetMethod,
      `${organizationApiServiceUrl}/GetMainClientById?id=${mainClientId}`,
      user.token
    );
    if (response.status === 200) {
      const { profile, status } = yield response.data
        .then((response) => ({ profile: response }))
        .catch(() => ({ status: 500 }));
      if (profile) {
        yield put(Actions.loadOrganizationMainClientSuccess(profile));
      } else {
        yield put(
          Actions.loadOrganizationMainClientFailed(GetErrorMessageFromStatus({ status: status }))
        );
      }
    } else {
      yield put(Actions.loadOrganizationMainClientFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.loadOrganizationMainClientFailed(GetErrorMessageFromStatus(error)));
  }
}

const SAVE_MAIN_CLIENT_FAILED_FALLBACK = "We couldn't save this client.";

function* failSaveOrganizationMainClient(message) {
  yield call(addNotification, { title: 'Client not saved', message, type: 'danger' });
  yield put(Actions.saveOrganizationMainClientFailed(message));
}

export function* saveOrganizationMainClient({
  payload: { id, name, city, state, country, codeId, groupId, contactEmail, contactName, logo },
}) {
  const user = getLoggedInUser();
  const data = new FormData();
  data.append('Id', id);
  data.append('Name', name);
  data.append('City', city);
  data.append('State', state);
  data.append('Country', country);
  data.append('CodeId', codeId);
  data.append('GroupId', groupId);
  data.append('ContactEmail', contactEmail);
  data.append('ContactName', contactName);
  data.append('Logo', logo);
  try {
    const response = yield call(
      fetchPostMethodWithFormData,
      `${organizationApiServiceUrl}/SaveOrganizationMainClient`,
      user.token,
      data
    );
    if (response.status === 200) {
      const { profile } = yield response.data
        .then((response) => ({ profile: response }))
        .catch(() => ({}));
      if (profile) {
        yield put(Actions.saveOrganizationMainClientSuccess(profile));
      } else {
        yield* failSaveOrganizationMainClient(SAVE_MAIN_CLIENT_FAILED_FALLBACK);
      }
    } else {
      const body = yield response.data;
      yield* failSaveOrganizationMainClient(
        getApiErrorMessage({ status: response.status, body }, SAVE_MAIN_CLIENT_FAILED_FALLBACK)
      );
    }
  } catch {
    yield* failSaveOrganizationMainClient(SAVE_MAIN_CLIENT_FAILED_FALLBACK);
  }
}

// Organization users.
function* loadOrganizationUsers() {
  try {
    const user = getLoggedInUser();
    const response = yield call(fetchGetMethod, `${organizationApiServiceUrl}/Users`, user.token);
    if (response.status === 200) {
      const { organizationListUsers, status } = yield response.data
        .then((response) => ({ organizationListUsers: response.comapanyListUsers }))
        .catch(() => ({ status: 500 }));
      if (organizationListUsers) {
        yield put(Actions.loadOrganizationUsersSuccess(organizationListUsers));
      } else {
        yield put(
          Actions.loadOrganizationUsersFailed(GetErrorMessageFromStatus({ status: status }))
        );
      }
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response.message }));
      yield put(
        Actions.loadOrganizationUsersFailed(message ?? GetErrorMessageFromStatus(response))
      );
    } else {
      yield put(Actions.loadOrganizationUsersFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.loadOrganizationUsersFailed(GetErrorMessageFromStatus(error)));
  }
}

function* inviteUserToOrganization({ payload: { userEmail } }) {
  try {
    const user = getLoggedInUser();
    const body = {
      Email: userEmail,
    };
    const response = yield call(
      fetchPostMethod,
      `${organizationApiServiceUrl}/InviteUser`,
      user.token,
      body,
      /* withoutResponseBody */ false
    );
    if (response.status === 200) {
      const { successMessage } = yield response.data.then((response) => ({
        successMessage: response.message,
      }));
      if (successMessage) {
        yield put(Actions.inviteUserToOrganizationSuccess(successMessage));
      } else {
        yield put(
          Actions.inviteUserToOrganizationFailed(GetErrorMessageFromStatus({ status: 500 }))
        );
      }
    } else if (response.status === 400) {
      const message = yield response.data
        .then((body) => body?.message ?? body?.Message ?? body?.title ?? body?.Title)
        .catch(() => null);
      if (message) {
        addNotification({
          title: 'Action not completed',
          message,
          type: 'danger',
        });
      }
      yield put(
        Actions.inviteUserToOrganizationFailed(message ?? GetErrorMessageFromStatus(response))
      );
    } else {
      yield put(Actions.inviteUserToOrganizationFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.inviteUserToOrganizationFailed(GetErrorMessageFromStatus(error)));
  }
}

function* resendOrganizationInvitation({ payload: { invitationId } }) {
  try {
    const user = getLoggedInUser();
    const body = {
      InvitationId: invitationId,
    };
    const response = yield call(
      fetchPostMethod,
      `${organizationApiServiceUrl}/ResendInvitation`,
      user.token,
      body,
      /* withoutResponseBody */ false
    );
    if (response.status === 200) {
      const body = yield response.data.then((d) => d).catch(() => ({}));
      const successMessage =
        body?.message ?? body?.Message ?? 'Invitation resent successfully.';
      // ListUsers shows success toast from actionSuccessMessage (same path as invite / other org actions).
      yield put(Actions.resendOrganizationInvitationSuccess(successMessage));
    } else if (response.status === 400) {
      const message = yield response.data
        .then((body) => body?.message ?? body?.Message ?? body?.title ?? body?.Title)
        .catch(() => null);
      yield put(
        Actions.resendOrganizationInvitationFailed(message ?? GetErrorMessageFromStatus(response))
      );
    } else {
      yield put(Actions.resendOrganizationInvitationFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.resendOrganizationInvitationFailed(GetErrorMessageFromStatus(error)));
  }
}

function* cancelOrganizationInvitation({ payload: { invitationId } }) {
  try {
    const user = getLoggedInUser();
    const response = yield call(fetchJSON, `${organizationApiServiceUrl}/CancelInvitation/${invitationId}`, {
      method: 'DELETE',
      headers: {
        Authorization: 'Bearer ' + user.token,
        Accept: 'application/json',
      },
    });
    if (response.status === 200) {
      const body = yield response.data.then((d) => d).catch(() => ({}));
      const successMessage =
        body?.message ?? body?.Message ?? 'Invitation cancelled successfully.';
      yield put(Actions.cancelOrganizationInvitationSuccess(successMessage));
    } else if (response.status === 400) {
      const message = yield response.data
        .then((body) => body?.message ?? body?.Message ?? body?.title ?? body?.Title)
        .catch(() => null);
      yield put(
        Actions.cancelOrganizationInvitationFailed(message ?? GetErrorMessageFromStatus(response))
      );
    } else {
      yield put(Actions.cancelOrganizationInvitationFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.cancelOrganizationInvitationFailed(GetErrorMessageFromStatus(error)));
  }
}

function* removeUserManagerFlag({ payload: { userId } }) {
  try {
    const user = getLoggedInUser();
    const body = {
      UserId: userId,
    };
    const response = yield call(
      fetchPostMethod,
      `${organizationApiServiceUrl}/RemoveManagerFlag`,
      user.token,
      body,
      /* withoutResponseBody */ false
    );
    if (response.status === 200) {
      const { successMessage } = yield response.data.then((response) => ({
        successMessage: response.message,
      }));
      if (successMessage) {
        yield put(Actions.removeUserManagerFlagSuccess(successMessage));
      } else {
        yield put(Actions.removeUserManagerFlagFailed(GetErrorMessageFromStatus({ status: 500 })));
      }
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response.message }));
      yield put(
        Actions.removeUserManagerFlagFailed(message ?? GetErrorMessageFromStatus(response))
      );
    } else {
      yield put(Actions.removeUserManagerFlagFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.removeUserManagerFlagFailed(GetErrorMessageFromStatus(error)));
  }
}

function* makeUserManager({ payload: { userId } }) {
  try {
    const user = getLoggedInUser();
    const body = {
      UserId: userId,
    };
    const response = yield call(
      fetchPostMethod,
      `${organizationApiServiceUrl}/MakeUserManager`,
      user.token,
      body,
      /* withoutResponseBody */ false
    );
    if (response.status === 200) {
      const { successMessage } = yield response.data.then((response) => ({
        successMessage: response.message,
      }));
      if (successMessage) {
        yield put(Actions.makeUserManagerSuccess(successMessage));
      } else {
        yield put(Actions.makeUserManagerFailed(GetErrorMessageFromStatus({ status: 500 })));
      }
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response.message }));
      yield put(Actions.makeUserManagerFailed(message ?? GetErrorMessageFromStatus(response)));
    } else {
      yield put(Actions.makeUserManagerFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.makeUserManagerFailed(GetErrorMessageFromStatus(error)));
  }
}

function* associateLicense({ payload: { userId, planId } }) {
  try {
    const user = getLoggedInUser();
    const body = {
      UserId: userId,
      PlanId: planId,
    };
    const response = yield call(
      fetchPostMethod,
      `${organizationApiServiceUrl}/AssociateLicense`,
      user.token,
      body,
      /* withoutResponseBody */ false
    );
    if (response.status === 200) {
      const { successMessage } = yield response.data.then((response) => ({
        successMessage: response.message,
      }));
      if (successMessage) {
        yield put(Actions.associateLicenseSuccess(successMessage));
      } else {
        yield put(Actions.associateLicenseFailed(GetErrorMessageFromStatus({ status: 500 })));
      }
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response.message }));
      yield put(Actions.associateLicenseFailed(message ?? GetErrorMessageFromStatus(response)));
    } else {
      yield put(Actions.associateLicenseFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.associateLicenseFailed(GetErrorMessageFromStatus(error)));
  }
}

function* cancelLicense({ payload: { userId } }) {
  try {
    const user = getLoggedInUser();
    const body = {
      UserId: userId,
    };
    const response = yield call(
      fetchPostMethod,
      `${organizationApiServiceUrl}/CancelLicense`,
      user.token,
      body,
      /* withoutResponseBody */ false
    );
    if (response.status === 200) {
      const { successMessage } = yield response.data.then((response) => ({
        successMessage: response.message,
      }));
      if (successMessage) {
        yield put(Actions.cancelLicenseSuccess(successMessage));
      } else {
        yield put(Actions.cancelLicenseFailed(GetErrorMessageFromStatus({ status: 500 })));
      }
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response.message }));
      yield put(Actions.cancelLicenseFailed(message ?? GetErrorMessageFromStatus(response)));
    } else {
      yield put(Actions.cancelLicenseFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.cancelLicenseFailed(GetErrorMessageFromStatus(error)));
  }
}

function* removeUserFromOrganization({ payload: { userId } }) {
  try {
    const user = getLoggedInUser();
    const body = {
      UserId: userId,
    };
    const response = yield call(
      fetchPostMethod,
      `${organizationApiServiceUrl}/RemoveUserFromOrganization`,
      user.token,
      body,
      /* withoutResponseBody */ false
    );
    if (response.status === 200) {
      const { successMessage, recoveryCode } = yield response.data.then((response) => ({
        successMessage: response.message,
        recoveryCode: response.recoveryCode || null,
      }));
      if (successMessage) {
        yield put(Actions.removeUserFromOrganizationSuccess(successMessage, recoveryCode, userId));
      } else {
        yield put(
          Actions.removeUserFromOrganizationFailed(GetErrorMessageFromStatus({ status: 500 }))
        );
      }
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response.message }));
      yield put(
        Actions.removeUserFromOrganizationFailed(message ?? GetErrorMessageFromStatus(response))
      );
    } else {
      yield put(Actions.removeUserFromOrganizationFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.removeUserFromOrganizationFailed(GetErrorMessageFromStatus(error)));
  }
}

// Organization Dashboard.
function* loadOrganizationDashboard() {
  try {
    const user = getLoggedInUser();
    const response = yield call(
      fetchGetMethod,
      `${dashboardApiServiceUrl}/OrganizationDashboard`,
      user.token
    );
    if (response.status === 200) {
      const {
        isOwner,
        vpoTotalImplementation,
        vpoTotalForecast,
        vpoTotalProposition,
        totalClients,
        totalValueProposition,
        totalPortifolioAmount,
        totalFundamentals,
        valuePropositionSummary,
        usersByPlan,
        status,
      } = yield response.data
        .then((response) => {
          return response.status === 200
            ? {
                isOwner: response.isOwner,
                vpoTotalImplementation: response.vpoTotalImplementation,
                vpoTotalForecast: response.vpoTotalForecast,
                vpoTotalProposition: response.vpoTotalProposition,
                totalClients: response.totalClients,
                totalValueProposition: response.totalValueProposition,
                totalPortifolioAmount: response.totalPortifolioAmount,
                totalFundamentals: response.totalFundamentals,
                valuePropositionSummary: response.valuePropositionSummary,
                usersByPlan: response.usersByPlan ?? response.UsersByPlan ?? {},
              }
            : { status: response.status };
        })
        .catch(() => ({ status: 500 }));
      if (
        vpoTotalImplementation &&
        vpoTotalForecast &&
        vpoTotalProposition &&
        typeof totalClients !== 'undefined' &&
        typeof totalValueProposition !== 'undefined' &&
        valuePropositionSummary
      ) {
        yield put(
          Actions.loadOrganizationDashboardDataSuccess(
            isOwner,
            vpoTotalImplementation,
            vpoTotalForecast,
            vpoTotalProposition,
            totalClients,
            totalValueProposition,
            totalPortifolioAmount,
            totalFundamentals,
            valuePropositionSummary,
            usersByPlan
          )
        );
      } else {
        yield put(
          Actions.loadOrganizationDashboardDataFailed(GetErrorMessageFromStatus({ status: status }))
        );
      }
    } else {
      yield put(Actions.loadOrganizationDashboardDataFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.loadOrganizationDashboardDataFailed(GetErrorMessageFromStatus(error)));
  }
}

// Value proposition.
function* loadOrganizationValuePropositions() {
  try {
    const user = getLoggedInUser();
    const response = yield call(
      fetchGetMethod,
      `${organizationApiServiceUrl}/GetValuePropositions`,
      user.token
    );
    if (response.status === 200) {
      const { organizationVpList } = yield response.data.then((response) => ({
        organizationVpList: { ...response.organizationValueProprosition, users: response.users },
      }));
      if (organizationVpList) {
        yield put(Actions.loadOrganizationValuePropositionsSuccess(organizationVpList));
      } else {
        yield put(
          Actions.loadOrganizationValuePropositionsFailed(
            GetErrorMessageFromStatus({ status: 500 })
          )
        );
      }
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response.message }));
      yield put(
        Actions.loadOrganizationValuePropositionsFailed(
          message ?? GetErrorMessageFromStatus(response)
        )
      );
    } else {
      yield put(
        Actions.loadOrganizationValuePropositionsFailed(GetErrorMessageFromStatus(response))
      );
    }
  } catch (error) {
    yield put(Actions.loadOrganizationValuePropositionsFailed(GetErrorMessageFromStatus(error)));
  }
}

function* getOrganizationValuePropositionDetails({ payload: { vpId } }) {
  try {
    const user = getLoggedInUser();
    const body = { ValuePropositionId: vpId };
    const response = yield call(
      fetchPostMethod,
      `${organizationApiServiceUrl}/VisualizeValueProposition`,
      user.token,
      body,
      /* withoutResponseBody */ false
    );
    if (response.status === 200) {
      const { organizationVpDetails } = yield response.data.then((response) => ({
        organizationVpDetails: response,
      }));
      if (organizationVpDetails) {
        yield put(
          Actions.getOrganizationValuePropositionDetailsSuccess(vpId, organizationVpDetails)
        );
      } else {
        yield put(
          Actions.getOrganizationValuePropositionDetailsFailed(
            GetErrorMessageFromStatus({ status: 500 })
          )
        );
      }
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response.message }));
      yield put(
        Actions.getOrganizationValuePropositionDetailsFailed(
          message ?? GetErrorMessageFromStatus(response)
        )
      );
    } else {
      yield put(
        Actions.getOrganizationValuePropositionDetailsFailed(GetErrorMessageFromStatus(response))
      );
    }
  } catch (error) {
    yield put(
      Actions.getOrganizationValuePropositionDetailsFailed(GetErrorMessageFromStatus(error))
    );
  }
}

// Normalize the bulk-reassign summary tolerating camelCase or PascalCase.
const normalizeBulkSummary = (summary, httpStatus) => ({
  status: summary?.status ?? summary?.Status ?? httpStatus,
  message: summary?.message ?? summary?.Message ?? '',
  succeeded: summary?.succeeded ?? summary?.Succeeded ?? [],
  failed: (summary?.failed ?? summary?.Failed ?? []).map((f) => ({
    id: f?.id ?? f?.Id,
    message: f?.message ?? f?.Message ?? '',
  })),
});

export function* reassignValueProposition({ payload: { valuePropositionId, targetUserId } }) {
  try {
    const user = getLoggedInUser();
    const body = { ValuePropositionId: valuePropositionId, TargetUserId: targetUserId };
    const response = yield call(
      fetchPostMethod,
      `${organizationApiServiceUrl}/ReassignValueProposition`,
      user.token,
      body,
      /* withoutResponseBody */ false
    );
    if (response.status === 200) {
      const { message } = yield response.data.then((r) => ({
        message: r?.message ?? r?.Message,
      }));
      yield put(Actions.reassignValuePropositionSuccess(message ?? 'Value proposition reassigned.'));
      yield put(Actions.loadOrganizationValuePropositions());
    } else if (response.status === 400) {
      const { message } = yield response.data.then((r) => ({
        message: r?.message ?? r?.Message,
      }));
      yield put(
        Actions.reassignValuePropositionFailed(message ?? GetErrorMessageFromStatus(response))
      );
    } else {
      yield put(Actions.reassignValuePropositionFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.reassignValuePropositionFailed(GetErrorMessageFromStatus(error)));
  }
}

export function* reassignValuePropositions({ payload: { valuePropositionIds, targetUserId } }) {
  try {
    const user = getLoggedInUser();
    const body = { ValuePropositionIds: valuePropositionIds, TargetUserId: targetUserId };
    const response = yield call(
      fetchBasicPostMethodWithToken,
      `${organizationApiServiceUrl}/ReassignValuePropositions`,
      user.token,
      body
    );
    const raw = yield response.data.then((r) => r).catch(() => null);
    const summary = normalizeBulkSummary(raw, response.status);
    if (response.status === 200 || response.status === 207) {
      yield put(Actions.reassignValuePropositionsSuccess(summary));
      if ((summary.succeeded?.length ?? 0) > 0) {
        yield put(Actions.loadOrganizationValuePropositions());
      }
    } else {
      yield put(Actions.reassignValuePropositionsFailed(summary));
    }
  } catch (error) {
    yield put(
      Actions.reassignValuePropositionsFailed({
        status: 500,
        message: GetErrorMessageFromStatus(error) ?? 'Internal Server Error',
        succeeded: [],
        failed: [],
      })
    );
  }
}

// Licenses
function* changeOrganizationUserLicenses({
  payload: { numberOfLicenses, licenseType, isUpgrade, quantity },
}) {
  try {
    const user = getLoggedInUser();
    const body = {
      NumberOfLicenses: numberOfLicenses,
      LicenseType: licenseType,
    };
    const response = yield call(
      fetchPostMethod,
      `${organizationApiServiceUrl}/UpgradeOrganizationUserLicenses`,
      user.token,
      body,
      /* withoutResponseBody */ false
    );
    if (response.status === 200) {
      const data = yield response.data.then((d) => d);
      const payload = {
        ...data,
        checkoutSession: data.checkoutSession ?? data.CheckoutSession ?? '',
        publicKey: data.publicKey ?? data.PublicKey,
        purchaseAppliedWithoutCheckout: !!(
          data.purchaseAppliedWithoutCheckout ?? data.PurchaseAppliedWithoutCheckout
        ),
        proratedAmountDueCents:
          data.proratedAmountDueCents ?? data.ProratedAmountDueCents ?? null,
        isUpgrade,
      };
      yield put(Actions.changeOrganizationUserLicensesSuccess(payload));
      if (payload.purchaseAppliedWithoutCheckout) {
        yield put(Actions.loadOrganizationDetails());
        yield put(Actions.loadOrganizationDashboardData());
      }
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response.message }));
      yield put(
        Actions.changeOrganizationUserLicensesFailed(message ?? GetErrorMessageFromStatus(response))
      );
    } else {
      yield put(Actions.changeOrganizationUserLicensesFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.changeOrganizationUserLicensesFailed(GetErrorMessageFromStatus(error)));
  }
}

function* userLicenseSuccessStrip(action) {
  try {
    const user = getLoggedInUser();
    const sessionId = action?.payload;
    let url = `${organizationApiServiceUrl}/UserLicenseSuccess`;
    if (sessionId) {
      url += `?session_id=${encodeURIComponent(sessionId)}`;
    }
    const response = yield call(fetchGetMethod, url, user.token);
    if (response.status === 200) {
      yield put(
        Actions.userLicenseSuccessStripSuccess("Congratulations!! You've purchased more licenses.")
      );
      // UserLicenseSuccess persists Stripe quantities server-side; refetch so dashboard license counts update.
      yield put(Actions.loadOrganizationDetails());
      yield put(Actions.loadOrganizationDashboardData());
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response.message }));
      yield put(
        Actions.userLicenseSuccessStripFailed(message ?? GetErrorMessageFromStatus(response))
      );
    } else {
      yield put(Actions.userLicenseSuccessStripFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.userLicenseSuccessStripFailed(GetErrorMessageFromStatus(error)));
  }
}

function* upgradeLicenses({ payload: { numberOfLicenses, licenseType } }) {
  try {
    const user = getLoggedInUser();
    const body = {
      NumberOfLicenses: numberOfLicenses,
      LicenseType: licenseType,
    };
    const response = yield call(
      fetchPostMethod,
      `${organizationApiServiceUrl}/UpgradeLicenses`,
      user.token,
      body,
      /* withoutResponseBody */ false
    );
    if (response.status === 200) {
      const { successMessage } = yield response.data.then((response) => ({
        successMessage: response.message,
      }));
      if (successMessage) {
        yield put(Actions.upgradeLicensesSuccess(successMessage));
        yield put(Actions.loadOrganizationDetails());
        yield put(Actions.loadOrganizationDashboardData());
      } else {
        yield put(Actions.upgradeLicensesFailed(GetErrorMessageFromStatus({ status: 500 })));
      }
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response.message }));
      yield put(Actions.upgradeLicensesFailed(message ?? GetErrorMessageFromStatus(response)));
    } else {
      yield put(Actions.upgradeLicensesFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.upgradeLicensesFailed(GetErrorMessageFromStatus(error)));
  }
}

function* downgradeLicenses({ payload: { numberOfLicenses, licenseType } }) {
  try {
    const user = getLoggedInUser();
    const body = {
      NumberOfLicenses: numberOfLicenses,
      LicenseType: licenseType,
    };
    const response = yield call(
      fetchPostMethod,
      `${organizationApiServiceUrl}/DowngradeLicenses`,
      user.token,
      body,
      /* withoutResponseBody */ false
    );
    if (response.status === 200) {
      const { successMessage } = yield response.data.then((response) => ({
        successMessage: response.message,
      }));
      if (successMessage) {
        yield put(Actions.downgradeLicensesSuccess(successMessage));
        yield put(Actions.loadOrganizationDetails());
        yield put(Actions.loadOrganizationDashboardData());
      } else {
        yield put(Actions.downgradeLicensesFailed(GetErrorMessageFromStatus({ status: 500 })));
      }
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response.message }));
      yield put(Actions.downgradeLicensesFailed(message ?? GetErrorMessageFromStatus(response)));
    } else {
      yield put(Actions.downgradeLicensesFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.downgradeLicensesFailed(GetErrorMessageFromStatus(error)));
  }
}

// Invitations
function* getMyInvitations() {
  try {
    const user = getLoggedInUser();
    const response = yield call(
      fetchGetMethod,
      `${organizationApiServiceUrl}/GetMyInvitations`,
      user.token
    );
    if (response.status === 200) {
      const { invitations } = yield response.data.then((response) => ({ invitations: response }));
      // const invitations2 = [
      //     { organizationId: 1, organizationName: "Main Client Test", imagePath: "https://slemptm20.blob.core.windows.net/organization-image/3bb5eadd-6238-4781-85f4-81c28f6f7c37_Main%20Client%20Test%2011adb230e-4d84-49ab-b044-09510c4c964aRectangle%205.png" }
      // ]
      if (invitations) {
        yield put(Actions.getMyInvitationsSuccess(invitations));
      } else {
        yield put(Actions.getMyInvitationsFailed(GetErrorMessageFromStatus({ status: 500 })));
      }
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response.message }));
      yield put(Actions.getMyInvitationsFailed(message ?? GetErrorMessageFromStatus(response)));
    } else {
      yield put(Actions.getMyInvitationsFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.getMyInvitationsFailed(GetErrorMessageFromStatus(error)));
  }
}

function* acceptInvitation({ payload: { tkn } }) {
  try {
    const user = getLoggedInUser();
    const body = {
      tkn: tkn,
    };
    const response = yield call(
      fetchPostMethod,
      `${organizationApiServiceUrl}/AcceptInvitation`,
      user.token,
      body,
      /* withoutResponseBody */ false
    );
    if (response.status === 200) {
      const { successMessage, token } = yield response.data.then((response) => ({
        successMessage: response.message,
        token: response.token,
      }));
      if (successMessage && token) {
        yield put(Actions.acceptInvitationSuccess(successMessage, token, tkn));
      } else {
        yield put(Actions.acceptInvitationFailed(GetErrorMessageFromStatus({ status: 500 })));
      }
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response.message }));
      yield put(Actions.acceptInvitationFailed(message ?? GetErrorMessageFromStatus(response)));
    } else {
      yield put(Actions.acceptInvitationFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.acceptInvitationFailed(GetErrorMessageFromStatus(error)));
  }
}

function* rejectInvitation({ payload: { organizationId } }) {
  try {
    const user = getLoggedInUser();
    const body = {
      OrganizationId: organizationId,
    };
    const response = yield call(
      fetchPostMethod,
      `${organizationApiServiceUrl}/RejectInvitation`,
      user.token,
      body,
      /* withoutResponseBody */ false
    );
    if (response.status === 200) {
      const { successMessage } = yield response.data.then((response) => ({
        successMessage: response.message,
      }));
      if (successMessage) {
        yield put(Actions.rejectInvitationSuccess(successMessage, organizationId));
      } else {
        yield put(Actions.rejectInvitationFailed(GetErrorMessageFromStatus({ status: 500 })));
      }
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response.message }));
      yield put(Actions.rejectInvitationFailed(message ?? GetErrorMessageFromStatus(response)));
    } else {
      yield put(Actions.rejectInvitationFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.rejectInvitationFailed(GetErrorMessageFromStatus(error)));
  }
}
function normalizeAciPayload(data) {
  if (!data || typeof data !== 'object') {
    return { message: undefined, isSuccess: undefined };
  }
  const message = data.message ?? data.Message;
  const rawSuccess = data.isSuccess ?? data.IsSuccess;
  const isSuccess = rawSuccess === true || rawSuccess === 'true' || rawSuccess === 1;
  return { message, isSuccess };
}

function* acceptOrganizationInvitation({ payload: { tkn } }) {
  try {
    const encodedTkn = encodeURIComponent(tkn);
    const response = yield call(
      fetchGetMethodAsync,
      `${organizationApiServiceUrl}/aci?tkn=${encodedTkn}`
    );

    if (response.status === 200) {
      const { message, isSuccess } = normalizeAciPayload(response.data);

      if (isSuccess) {
        yield put(Actions.acceptOrganizationInvitationSuccess(message, isSuccess));
      } else {
        yield put(Actions.acceptOrganizationInvitationFailed(message, isSuccess));
      }
    } else if (response.status === 400) {
      const { message, isSuccess } = normalizeAciPayload(response.data);

      yield put(Actions.acceptOrganizationInvitationFailed(message, isSuccess));
    } else {
      yield put(Actions.acceptOrganizationInvitationFailed('Error', false));
    }
  } catch (error) {
    if (yield cancelled()) {
      return;
    }
    yield put(Actions.acceptOrganizationInvitationFailed('Error', false));
  }
}

function normalizeInvitationTokenDetails(data) {
  if (!data || typeof data !== 'object') {
    return {
      isValid: false,
      message: 'Invalid response.',
      organizationName: '',
      isDeclined: false,
      isExpired: false,
      alreadyJoinedOrganization: false,
    };
  }
  return {
    isValid: data.isValid ?? data.IsValid ?? false,
    message: data.message ?? data.Message ?? '',
    organizationName: data.organizationName ?? data.OrganizationName ?? '',
    isDeclined: data.isDeclined ?? data.IsDeclined ?? false,
    isExpired: data.isExpired ?? data.IsExpired ?? false,
    alreadyJoinedOrganization:
      data.alreadyJoinedOrganization ?? data.AlreadyJoinedOrganization ?? false,
  };
}

function* loadOrganizationInvitationTokenDetails({ payload: { tkn } }) {
  try {
    const encodedTkn = encodeURIComponent(tkn);
    const response = yield call(
      fetchGetMethodAsync,
      `${organizationApiServiceUrl}/invitation-details?tkn=${encodedTkn}`
    );
    if (response.status === 200) {
      const details = normalizeInvitationTokenDetails(response.data);
      yield put(Actions.loadOrganizationInvitationTokenDetailsSuccess(details));
    } else {
      yield put(
        Actions.loadOrganizationInvitationTokenDetailsFailed(
          GetErrorMessageFromStatus(response)
        )
      );
    }
  } catch (error) {
    yield put(
      Actions.loadOrganizationInvitationTokenDetailsFailed(GetErrorMessageFromStatus(error))
    );
  }
}

function* declineOrganizationInvitation({ payload: { tkn } }) {
  try {
    const encodedTkn = encodeURIComponent(tkn);
    const response = yield call(
      fetchGetMethodAsync,
      `${organizationApiServiceUrl}/dci?tkn=${encodedTkn}`
    );

    if (response.status === 200) {
      const { message, isSuccess } = normalizeAciPayload(response.data);
      if (isSuccess) {
        yield put(Actions.declineOrganizationInvitationSuccess(message, isSuccess));
      } else {
        yield put(Actions.declineOrganizationInvitationFailed(message, isSuccess));
      }
    } else if (response.status === 400) {
      const { message, isSuccess } = normalizeAciPayload(response.data);
      yield put(Actions.declineOrganizationInvitationFailed(message, isSuccess));
    } else {
      yield put(Actions.declineOrganizationInvitationFailed('Error', false));
    }
  } catch (error) {
    if (yield cancelled()) {
      return;
    }
    yield put(Actions.declineOrganizationInvitationFailed('Error', false));
  }
}

function* checkOwnerAndGetManagers() {
  try {
    const user = getLoggedInUser();
    const response = yield call(
      fetchGetMethod,
      `${organizationApiServiceUrl}/CheckOwnerAndGetManagers`,
      user.token
    );
    if (response.status === 200) {
      const ownerCheck = yield response.data.then((response) => response);
      yield put(Actions.checkOwnerAndGetManagersSuccess(ownerCheck));
    } else {
      yield put(Actions.checkOwnerAndGetManagersFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.checkOwnerAndGetManagersFailed(GetErrorMessageFromStatus(error)));
  }
}

function* transferOwnership({ payload: { transferToUserId } }) {
  try {
    const user = getLoggedInUser();
    const body = {
      TransferToUserId: transferToUserId,
    };
    const response = yield call(
      fetchPostMethod,
      `${organizationApiServiceUrl}/TransferOwnership`,
      user.token,
      body,
      false
    );
    if (response.status === 200) {
      const { message } = yield response.data.then((response) => ({
        message: response.message,
      }));
      yield put(Actions.transferOwnershipSuccess(message));
    } else {
      yield put(Actions.transferOwnershipFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.transferOwnershipFailed(GetErrorMessageFromStatus(error)));
  }
}

function* deactivateOrganization() {
  try {
    const user = getLoggedInUser();
    const response = yield call(
      fetchPostMethod,
      `${organizationApiServiceUrl}/DeactivateOrganization`,
      user.token,
      {},
      false
    );
    if (response.status === 200) {
      const responseData = yield response.data;
      const recoveryCode = responseData?.recoveryCode || responseData?.RecoveryCode || null;
      const message = responseData?.message || responseData?.Message || 'Organization deactivated successfully';
      yield put(Actions.deactivateOrganizationSuccess(recoveryCode, message));
    } else {
      yield put(Actions.deactivateOrganizationFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.deactivateOrganizationFailed(GetErrorMessageFromStatus(error)));
  }
}

function* sendRecoveryCodeEmail() {
  try {
    const user = getLoggedInUser();
    const response = yield call(
      fetchPostMethod,
      `${organizationApiServiceUrl}/SendRecoveryCodeEmail`,
      user.token,
      {},
      false
    );
    if (response.status === 200) {
      const { message } = yield response.data.then((response) => ({
        message: response.message,
      }));
      yield put(Actions.sendRecoveryCodeEmailSuccess(message));
    } else {
      const { message } = yield response.data.then((response) => ({
        message: response.message || GetErrorMessageFromStatus(response),
      }));
      yield put(Actions.sendRecoveryCodeEmailFailed(message));
    }
  } catch (error) {
    yield put(Actions.sendRecoveryCodeEmailFailed(GetErrorMessageFromStatus(error)));
  }
}

function* reactivateOrganization({ payload: { recoveryCode } }) {
  const EXPIRED_CODE_MESSAGE =
    'This recovery code has expired. Recovery codes expire after 30 days. Please contact support for assistance.';

  const safeErrorMessage = (errorPayload, fallback = 'Unable to reactivate organization.') => {
    if (!errorPayload) {
      return fallback;
    }

    if (typeof errorPayload === 'string') {
      return errorPayload;
    }

    if (typeof errorPayload?.message === 'string' && errorPayload.message.trim() !== '') {
      return errorPayload.message;
    }

    if (typeof errorPayload?.error === 'string' && errorPayload.error.trim() !== '') {
      return errorPayload.error;
    }

    if (typeof errorPayload?.title === 'string' && errorPayload.title.trim() !== '') {
      return errorPayload.title;
    }

    const statusMessage = GetErrorMessageFromStatus(errorPayload);
    if (typeof statusMessage === 'string' && statusMessage.trim() !== '') {
      return statusMessage;
    }

    return fallback;
  };

  try {
    const user = getLoggedInUser();
    const response = yield call(
      fetchBasicPostMethodWithToken,
      `${organizationApiServiceUrl}/ReactivateOrganization`,
      user.token,
      { recoveryCode }
    );
    if (response.status === 200) {
      const { message, organizationId, organizationName } = yield response.data.then((response) => ({
        message: response.message,
        organizationId: response.organizationId,
        organizationName: response.organizationName,
      }));
      yield put(Actions.reactivateOrganizationSuccess(message, organizationId, organizationName));
    } else {
      let errorPayload = null;
      try {
        errorPayload = yield response.data;
      } catch (parseError) {
        errorPayload = parseError;
      }

      const message = safeErrorMessage(errorPayload, GetErrorMessageFromStatus(response));
      yield put(Actions.reactivateOrganizationFailed(message));
    }
  } catch (error) {
    yield put(
      Actions.reactivateOrganizationFailed(
        safeErrorMessage(error, GetErrorMessageFromStatus(error) || EXPIRED_CODE_MESSAGE)
      )
    );
  }
}

export function* watchLoadOrganizationDetails(): any {
  yield takeLatest(ActionsType.LOAD_ORGANIZATION_DETAILS, loadOrganizationDetails);
}

export function* watchCreateOrganization(): any {
  yield takeLatest(ActionsType.CREATE_ORGANIZATION, createOrganization);
}

export function* watchEditOrganizationDetails(): any {
  yield takeLatest(ActionsType.EDIT_ORGANIZATION_DETAILS, editOrganizationDetails);
}

export function* watchLoadOrganizationClients(): any {
  yield takeLatest(ActionsType.LOAD_ORGANIZATION_CLIENTS, loadOrganizationClients);
}

export function* watchLoadOrganizationMainClient(): any {
  yield takeLatest(ActionsType.LOAD_ORGANIZATION_MAIN_CLIENT, loadOrganizationMainClient);
}

export function* watchSaveOrganizationMainClient(): any {
  yield takeLatest(ActionsType.SAVE_ORGANIZATION_MAIN_CLIENT, saveOrganizationMainClient);
}

export function* watchLoadOrganizationUsers(): any {
  yield takeLatest(ActionsType.LOAD_ORGANIZATION_USERS, loadOrganizationUsers);
}

export function* watchInviteUserToOrganization(): any {
  yield takeLatest(ActionsType.INVITE_USER_TO_ORGANIZATION, inviteUserToOrganization);
}

export function* watchResendOrganizationInvitation(): any {
  yield takeLatest(ActionsType.RESEND_ORGANIZATION_INVITATION, resendOrganizationInvitation);
}

export function* watchCancelOrganizationInvitation(): any {
  yield takeLatest(ActionsType.CANCEL_ORGANIZATION_INVITATION, cancelOrganizationInvitation);
}

export function* watchMakeUserManager(): any {
  yield takeLatest(ActionsType.MAKE_USER_MANAGER, makeUserManager);
}

export function* watchRemoveUserManagerFlag(): any {
  yield takeLatest(ActionsType.REMOVE_USER_MANAGER_FLAG, removeUserManagerFlag);
}

export function* watchAssociateLicense(): any {
  yield takeLatest(ActionsType.ASSOCIATE_LICENSE, associateLicense);
}

export function* watchCancelLicense(): any {
  yield takeLatest(ActionsType.CANCEL_LICENSE, cancelLicense);
}

export function* watchRemoveUserFromOrganization(): any {
  yield takeLatest(ActionsType.REMOVE_USER_FROM_ORGANIZATION, removeUserFromOrganization);
}

export function* watchLoadOrganizationDashboard(): any {
  yield takeLatest(ActionsType.LOAD_ORGANIZATION_DASHBOARD_DATA, loadOrganizationDashboard);
}

export function* watchLoadOrganizationValuePropositions(): any {
  yield takeLatest(
    ActionsType.LOAD_ORGANIZATION_VALUE_PROPOSITIONS,
    loadOrganizationValuePropositions
  );
}

export function* watchGetOrganizationValuePropositionDetails(): any {
  yield takeLatest(
    ActionsType.GET_ORGANIZATION_VALUE_PROPOSITION_DETAILS,
    getOrganizationValuePropositionDetails
  );
}

export function* watchReassignValueProposition(): any {
  yield takeLatest(ActionsType.REASSIGN_VALUE_PROPOSITION, reassignValueProposition);
}

export function* watchReassignValuePropositions(): any {
  yield takeLatest(ActionsType.REASSIGN_VALUE_PROPOSITIONS, reassignValuePropositions);
}

export function* watchUpgradeLicenses(): any {
  yield takeLatest(ActionsType.ORGANIZATION_UPGRADE_LICENSES, upgradeLicenses);
}

export function* watchDowngradeLicenses(): any {
  yield takeLatest(ActionsType.ORGANIZATION_DOWNGRADE_LICENSES, downgradeLicenses);
}

export function* watchChangeOrganizationUserLicenses(): any {
  yield takeLatest(ActionsType.CHANGE_ORGANIZATION_USER_LICENSES, changeOrganizationUserLicenses);
}

export function* watchUserLicenseSuccessStrip(): any {
  yield takeLatest(ActionsType.ORGANIZATION_USER_LICENSE_SUCCESS_STRIPE, userLicenseSuccessStrip);
}

export function* watchGetMyInvitations(): any {
  yield takeLatest(ActionsType.GET_MY_INVITATIONS, getMyInvitations);
}

export function* watchAcceptInvitation(): any {
  yield takeLatest(ActionsType.ACCEPT_INVITATION, acceptInvitation);
}
export function* watchRejectInvitation(): any {
  yield takeLatest(ActionsType.REJECT_INVITATION, rejectInvitation);
}
export function* watchAcceptOrganizationInvitation(): any {
  // takeLeading: a second ACCEPT while the first HTTP call is in flight must not cancel the first
  // (takeLatest was dropping the in-flight accept and surfacing a bogus failure while the retry succeeded).
  yield takeLeading(ActionsType.ACCEPT_ORGANIZATION_INVITATION, acceptOrganizationInvitation);
}

export function* watchLoadOrganizationInvitationTokenDetails(): any {
  yield takeLeading(
    ActionsType.LOAD_ORGANIZATION_INVITATION_TOKEN_DETAILS,
    loadOrganizationInvitationTokenDetails
  );
}

export function* watchDeclineOrganizationInvitation(): any {
  yield takeLeading(ActionsType.DECLINE_ORGANIZATION_INVITATION, declineOrganizationInvitation);
}

export function* watchCheckOwnerAndGetManagers(): any {
  yield takeLatest(ActionsType.CHECK_OWNER_AND_GET_MANAGERS, checkOwnerAndGetManagers);
}

export function* watchTransferOwnership(): any {
  yield takeLatest(ActionsType.TRANSFER_OWNERSHIP, transferOwnership);
}

export function* watchDeactivateOrganization(): any {
  yield takeLatest(ActionsType.DEACTIVATE_ORGANIZATION, deactivateOrganization);
}

export function* watchSendRecoveryCodeEmail(): any {
  yield takeLatest(ActionsType.SEND_RECOVERY_CODE_EMAIL, sendRecoveryCodeEmail);
}

function* generateRecoveryCodeAfterUnsubscribe() {
  try {
    const user = getLoggedInUser();
    const response = yield call(
      fetchPostMethod,
      `${organizationApiServiceUrl}/GenerateRecoveryCodeAfterUnsubscribe`,
      user.token,
      {},
      false
    );
    if (response.status === 200) {
      const { recoveryCode, message } = yield response.data.then((response) => ({
        recoveryCode: response.recoveryCode,
        message: response.message,
      }));
      yield put(Actions.generateRecoveryCodeAfterUnsubscribeSuccess(recoveryCode, message));
    } else {
      const { message } = yield response.data.then((response) => ({
        message: response.message || GetErrorMessageFromStatus(response),
      }));
      yield put(Actions.generateRecoveryCodeAfterUnsubscribeFailed(message));
    }
  } catch (error) {
    yield put(Actions.generateRecoveryCodeAfterUnsubscribeFailed(GetErrorMessageFromStatus(error)));
  }
}

export function* watchGenerateRecoveryCodeAfterUnsubscribe(): any {
  yield takeLatest(ActionsType.GENERATE_RECOVERY_CODE_AFTER_UNSUBSCRIBE, generateRecoveryCodeAfterUnsubscribe);
}

export function* watchReactivateOrganization(): any {
  yield takeLatest(ActionsType.REACTIVATE_ORGANIZATION, reactivateOrganization);
}

function* organizationSaga(): any {
  yield all([
    fork(watchAssociateLicense),
    fork(watchCancelLicense),
    fork(watchCreateOrganization),
    fork(watchDowngradeLicenses),
    fork(watchEditOrganizationDetails),
    fork(watchGetOrganizationValuePropositionDetails),
    fork(watchInviteUserToOrganization),
    fork(watchResendOrganizationInvitation),
    fork(watchCancelOrganizationInvitation),
    fork(watchLoadOrganizationDashboard),
    fork(watchLoadOrganizationDetails),
    fork(watchLoadOrganizationClients),
    fork(watchLoadOrganizationMainClient),
    fork(watchSaveOrganizationMainClient),
    fork(watchLoadOrganizationUsers),
    fork(watchLoadOrganizationValuePropositions),
    fork(watchReassignValueProposition),
    fork(watchReassignValuePropositions),
    fork(watchMakeUserManager),
    fork(watchRemoveUserFromOrganization),
    fork(watchRemoveUserManagerFlag),
    fork(watchUpgradeLicenses),
    fork(watchChangeOrganizationUserLicenses),
    fork(watchUserLicenseSuccessStrip),
    fork(watchGetMyInvitations),
    fork(watchAcceptInvitation),
    fork(watchRejectInvitation),
    fork(watchAcceptOrganizationInvitation),
    fork(watchLoadOrganizationInvitationTokenDetails),
    fork(watchDeclineOrganizationInvitation),
    fork(watchCheckOwnerAndGetManagers),
    fork(watchTransferOwnership),
    fork(watchDeactivateOrganization),
    fork(watchSendRecoveryCodeEmail),
    fork(watchGenerateRecoveryCodeAfterUnsubscribe),
    fork(watchReactivateOrganization),
  ]);
}

export default organizationSaga;
